// Bautagebuch rules, client twin of utils/siteDiary/calc.js (hours and merged view while
// editing; the server stays authoritative). Keep everything below the imports identical to the
// server file: test/siteDiary/calc.test.js compares both.

import { parseClock, formatClock, spanMinutes } from "../workReports/calc";

const SECTIONS = [
  "workPerformed",
  "materialsDelivered",
  "instructions",
  "specialEvents",
  "visitors"
];
const OBSTRUCTION_KINDS = ["OBSTRUCTION", "CONCERN"];
const SIGNATURE_ROLES = ["SITE_MANAGER", "CLIENT"];
const REMINDER_MODES = ["workdays", "attendance"];
const MAX_ROWS = 100;
const MAX_SECTION_CHARS = 20000;
const MINUTES_PER_DAY = 1440;
const DAY_MS = 86400000;
// 0 = a page locks only when it is signed
const MAX_LOCK_HOURS = 720;
const DEFAULT_SETTINGS = {
  lockAfterHours: 48,
  reminders: true,
  reminderMode: "workdays",
  reminderDays: 5
};

const DAY_FORMAT = /^\d{4}-\d{2}-\d{2}$/;

/** A real calendar day "YYYY-MM-DD" (2026-02-30 is not). */
const isDay = value => {
  if (!DAY_FORMAT.test(String(value || ""))) return false;
  const time = Date.parse(`${value}T00:00:00Z`);
  return !Number.isNaN(time) && new Date(time).toISOString().slice(0, 10) === value;
};

const addDays = (day, n) =>
  new Date(Date.parse(`${day}T00:00:00Z`) + n * DAY_MS).toISOString().slice(0, 10);

/** 0 = Sunday … 6 = Saturday. */
const weekday = day => new Date(`${day}T00:00:00Z`).getUTCDay();

const toInt = (value, { min = 0, max = Number.MAX_SAFE_INTEGER } = {}) => {
  const n = Math.round(Number(value));
  if (!Number.isFinite(n)) return min;
  return Math.min(max, Math.max(min, n));
};

const clean = (value, max) =>
  String(value ?? "")
    .trim()
    .slice(0, max);

const idOf = value => (value && value._id ? String(value._id) : value ? String(value) : "");

/** Company settings with defaults. */
const normalizeSettings = (raw = {}) => {
  const source = raw || {};
  const hours = Number(source.lockAfterHours);
  return {
    lockAfterHours: Number.isFinite(hours)
      ? toInt(hours, { max: MAX_LOCK_HOURS })
      : DEFAULT_SETTINGS.lockAfterHours,
    reminders: source.reminders === undefined ? true : Boolean(source.reminders),
    reminderMode: REMINDER_MODES.includes(source.reminderMode)
      ? source.reminderMode
      : DEFAULT_SETTINGS.reminderMode,
    reminderDays: source.reminderDays
      ? toInt(source.reminderDays, { min: 1, max: 30 })
      : DEFAULT_SETTINGS.reminderDays
  };
};

// ---------------------------------------------------------------------------------------------
// Staff and hours
// ---------------------------------------------------------------------------------------------

/** Keeps what a staff row may hold; with start and end the minutes are derived. */
const normalizeStaff = (raw = {}) => {
  const row = raw || {};
  const start = parseClock(row.start);
  const end = parseClock(row.end);
  const out = {
    employee: row.employee ? idOf(row.employee) : null,
    name: clean(row.name, 120),
    trade: clean(row.trade, 80),
    start: start === null ? "" : formatClock(start),
    end: end === null ? "" : formatClock(end),
    breakMinutes: toInt(row.breakMinutes, { max: MINUTES_PER_DAY }),
    minutes: toInt(row.minutes, { max: MINUTES_PER_DAY }),
    note: clean(row.note, 300),
    source: row.source === "ATTENDANCE" ? "ATTENDANCE" : "MANUAL"
  };
  if (row._id) out._id = row._id;
  if (out.start && out.end) {
    out.minutes = Math.max(0, spanMinutes(out.start, out.end) - out.breakMinutes);
  }
  return out;
};

/**
 * Staff rows proposed from the attendance (Arbeitszeitnachweis) merged into the rows of the day:
 * a row typed by hand always wins, an earlier attendance row of the same person is replaced,
 * people who only appear in the attendance are added.
 */
const mergeStaff = (current = [], proposed = []) => {
  const rows = (current || []).map(normalizeStaff);
  const manual = new Set(
    rows.filter(r => r.source === "MANUAL" && r.employee).map(r => String(r.employee))
  );
  const fresh = (proposed || [])
    .map(p => normalizeStaff({ ...p, source: "ATTENDANCE" }))
    .filter(p => !p.employee || !manual.has(String(p.employee)));
  const replaced = new Set(fresh.map(p => String(p.employee || "")).filter(Boolean));
  const kept = rows.filter(
    r => !(r.source === "ATTENDANCE" && r.employee && replaced.has(String(r.employee)))
  );
  return [...kept, ...fresh];
};

/** Head count and minutes of a day's staff; a person listed twice counts once. */
const aggregateHours = (staff = []) => {
  const rows = (staff || []).map(normalizeStaff);
  const people = new Map();
  for (const row of rows) {
    const key = row.employee ? `e:${row.employee}` : `n:${row.name.toLowerCase()}`;
    if (key === "n:") continue;
    const person = people.get(key) || { employee: row.employee, name: row.name, minutes: 0 };
    person.minutes += row.minutes;
    people.set(key, person);
  }
  const list = [...people.values()];
  return {
    people: list.length,
    minutes: list.reduce((total, p) => total + p.minutes, 0),
    fromAttendance: rows.filter(r => r.source === "ATTENDANCE").length,
    byPerson: list
  };
};

/** Totals over several days (the PDF of a date range): days, person-days, minutes per person. */
const summarizeRange = (entries = []) => {
  const people = new Map();
  let minutes = 0;
  let personDays = 0;
  for (const entry of entries || []) {
    const day = aggregateHours(entry.staff);
    minutes += day.minutes;
    personDays += day.people;
    for (const person of day.byPerson) {
      const key = person.employee ? `e:${person.employee}` : `n:${person.name.toLowerCase()}`;
      const total = people.get(key) || { name: person.name, minutes: 0, days: 0 };
      total.minutes += person.minutes;
      total.days += 1;
      people.set(key, total);
    }
  }
  return {
    days: (entries || []).length,
    personDays,
    minutes,
    byPerson: [...people.values()].sort((a, b) => a.name.localeCompare(b.name, "de"))
  };
};

// ---------------------------------------------------------------------------------------------
// Contributions of several authors
// ---------------------------------------------------------------------------------------------

const normalizeSubcontractor = (raw = {}) => ({
  company: clean(raw?.company, 160),
  trade: clean(raw?.trade, 80),
  workers: toInt(raw?.workers, { max: 500 }),
  note: clean(raw?.note, 300)
});

const normalizeEquipment = (raw = {}) => ({
  name: clean(raw?.name, 160),
  quantity: toInt(raw?.quantity, { max: 1000 }) || 1,
  note: clean(raw?.note, 300)
});

/** One author's part of the day, in the stored format. */
const normalizeContribution = (raw = {}) => {
  const source = raw || {};
  const out = {};
  for (const key of SECTIONS) out[key] = clean(source[key], MAX_SECTION_CHARS);
  out.subcontractors = (Array.isArray(source.subcontractors) ? source.subcontractors : [])
    .slice(0, MAX_ROWS)
    .map(normalizeSubcontractor)
    .filter(s => s.company);
  out.equipment = (Array.isArray(source.equipment) ? source.equipment : [])
    .slice(0, MAX_ROWS)
    .map(normalizeEquipment)
    .filter(e => e.name);
  return out;
};

const isEmptyContribution = c =>
  SECTIONS.every(key => !c[key]) && !c.subcontractors.length && !c.equipment.length;

/**
 * The day as one document: per section the texts of every author (in the order they first
 * wrote), the subcontractors and equipment of all authors without duplicates (the same crew
 * reported twice counts once, with the larger head count).
 */
const mergeContributions = (contributions = []) => {
  const list = (contributions || [])
    .map(c => ({
      author: idOf(c.author),
      authorName: clean(c.authorName, 120),
      ...normalizeContribution(c)
    }))
    .filter(c => !isEmptyContribution(c));
  const sections = {};
  for (const key of SECTIONS) {
    sections[key] = list
      .filter(c => c[key])
      .map(c => ({ author: c.author, authorName: c.authorName, text: c[key] }));
  }
  const subcontractors = new Map();
  const equipment = new Map();
  for (const c of list) {
    for (const s of c.subcontractors) {
      const key = `${s.company.toLowerCase()}|${s.trade.toLowerCase()}`;
      const known = subcontractors.get(key);
      if (!known) subcontractors.set(key, { ...s });
      else {
        known.workers = Math.max(known.workers, s.workers);
        if (s.note && !known.note.includes(s.note)) {
          known.note = [known.note, s.note].filter(Boolean).join("; ");
        }
      }
    }
    for (const e of c.equipment) {
      const key = e.name.toLowerCase();
      const known = equipment.get(key);
      if (!known) equipment.set(key, { ...e });
      else {
        known.quantity = Math.max(known.quantity, e.quantity);
        if (e.note && !known.note.includes(e.note)) {
          known.note = [known.note, e.note].filter(Boolean).join("; ");
        }
      }
    }
  }
  return {
    authors: list.map(c => ({ author: c.author, authorName: c.authorName })),
    sections,
    subcontractors: [...subcontractors.values()],
    equipment: [...equipment.values()]
  };
};

/** One section as plain text: a single author's text as is, several prefixed with the name. */
const sectionText = parts => {
  if (!parts || !parts.length) return "";
  if (parts.length === 1) return parts[0].text;
  return parts.map(p => `${p.authorName || "?"}: ${p.text}`).join("\n\n");
};

// ---------------------------------------------------------------------------------------------
// Locking
// ---------------------------------------------------------------------------------------------

/** Offset of Europe/Berlin from UTC in minutes at an instant (60 or 120). */
const berlinOffset = time => {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-US", {
      timeZone: "Europe/Berlin",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23"
    })
      .formatToParts(new Date(time))
      .map(p => [p.type, p.value])
  );
  const local = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute)
  );
  return Math.round((local - Math.floor(time / 60000) * 60000) / 60000);
};

/** The instant a Berlin calendar day begins. */
const berlinMidnight = day => {
  const guess = Date.parse(`${day}T00:00:00Z`);
  return guess - berlinOffset(guess - 2 * 3600000) * 60000;
};

/**
 * Whether a page is evidence now. A signature by the site manager locks it at once; otherwise
 * it locks `lockAfterHours` after the end of its day (0 = only by signing).
 * { locked, reason: "SIGNED" | "TIME" | null, lockAt: ISO string | null }
 */
const lockState = (entry, settings, now = Date.now()) => {
  const { lockAfterHours } = normalizeSettings(settings);
  const signedAt = entry?.signatures?.siteManager?.signedAt;
  const lockAt =
    lockAfterHours && isDay(entry?.date)
      ? berlinMidnight(addDays(entry.date, 1)) + lockAfterHours * 3600000
      : null;
  if (signedAt) {
    return {
      locked: true,
      reason: "SIGNED",
      lockAt: new Date(signedAt).toISOString()
    };
  }
  const time = typeof now === "number" ? now : new Date(now).getTime();
  if (lockAt !== null && time >= lockAt) {
    return { locked: true, reason: "TIME", lockAt: new Date(lockAt).toISOString() };
  }
  return {
    locked: false,
    reason: null,
    lockAt: lockAt === null ? null : new Date(lockAt).toISOString()
  };
};

// ---------------------------------------------------------------------------------------------
// Missing days
// ---------------------------------------------------------------------------------------------

/**
 * Days of a project that should have a page but have none, oldest first, never today or later.
 * "workdays": every Monday to Friday between the project's start and end;
 * "attendance": only days on which someone booked time on the project.
 */
const missingDays = ({
  from,
  to,
  today,
  filled = [],
  mode = "workdays",
  attendanceDays = [],
  projectStart = "",
  projectEnd = ""
}) => {
  if (!isDay(from) || !isDay(to) || !isDay(today)) return [];
  const have = new Set(filled);
  const worked = new Set(attendanceDays);
  const first = isDay(projectStart) && projectStart > from ? projectStart : from;
  const yesterday = addDays(today, -1);
  let last = to < yesterday ? to : yesterday;
  if (isDay(projectEnd) && projectEnd < last) last = projectEnd;
  const days = [];
  for (let day = first; day <= last; day = addDays(day, 1)) {
    if (have.has(day)) continue;
    if (mode === "attendance") {
      if (worked.has(day)) days.push(day);
    } else if (weekday(day) !== 0 && weekday(day) !== 6) {
      days.push(day);
    }
    if (days.length > 400) break;
  }
  return days;
};

export {
  SECTIONS,
  OBSTRUCTION_KINDS,
  SIGNATURE_ROLES,
  REMINDER_MODES,
  MAX_ROWS,
  MAX_LOCK_HOURS,
  DEFAULT_SETTINGS,
  isDay,
  addDays,
  weekday,
  normalizeSettings,
  normalizeStaff,
  mergeStaff,
  aggregateHours,
  summarizeRange,
  normalizeSubcontractor,
  normalizeEquipment,
  normalizeContribution,
  mergeContributions,
  sectionText,
  berlinOffset,
  berlinMidnight,
  lockState,
  missingDays
};
